<script lang="ts">
  import type { Atelier } from '../../stores/creation.svelte'

  let {
    atelier,
    creer,
    refus,
  }: { atelier: Atelier; creer: () => void; refus: string[] } = $props()

  const capacites = $derived(atelier.referentiel.capacites)
  const region = $derived(
    atelier.referentiel.regions.find((r) => r.id === atelier.feuille.region),
  )
  const classe = $derived(
    atelier.referentiel.classes.find((c) => c.id === atelier.feuille.classe),
  )
  /** Rien à encadrer tant que ni la Région ni la Classe n'ont donné quelque chose. */
  const aDesCapacites = $derived(
    atelier.feuille.capacites.length + (classe?.capacites.length ?? 0) > 0,
  )
</script>

<div class="cartouche identite">
  <div class="bandeau">Identité</div>
  <div class="corps">
    <label>
      <span>Nom du personnage</span>
      <input
        class="saisie"
        type="text"
        value={atelier.feuille.nom}
        oninput={(e) => atelier.nommer(e.currentTarget.value)}
        placeholder="Kamila Thomas"
      />
    </label>
    <p class="origine">
      {#if region && classe}
        {classe.nom}{#if classe.faction}, {classe.faction}{/if} — originaire de {region.nom}
      {:else}
        Région et Classe restent à choisir.
      {/if}
    </p>
  </div>
</div>

<div class="cartouche">
  <div class="bandeau">Caractéristiques dérivées</div>
  <div class="corps">
    <p class="chapo">
      Rien de tout cela n'est stocké : tout se recalcule depuis les valeurs
      brutes, à chaque changement.
    </p>
    <dl class="derivees">
  {#each atelier.referentiel.derivees as d (d.id)}
    <div>
      <dt>{d.nom}</dt>
      <dd class="data">{atelier.etat.apercu.derivees[d.id] ?? 0}</dd>
    </div>
  {/each}
      <div>
        <dt>Chance</dt>
        <dd class="data">{atelier.referentiel.bareme.chance}</dd>
      </div>
    </dl>
  </div>
</div>

{#if aDesCapacites}
  <div class="cartouche">
    <div class="bandeau">Capacités</div>
  <div class="corps">
    <ul class="capacites">
  {#each atelier.feuille.capacites as c (c.capacite)}
    <li>
      <strong>{capacites[c.capacite]?.nom ?? c.capacite}</strong>
      <span class="voie">{c.d100 != null ? `tirée au ${c.d100}` : 'choisie'}</span>
      <span class="resume">{capacites[c.capacite]?.resume ?? ''}</span>
    </li>
  {/each}
        {#each classe?.capacites ?? [] as id (id)}
          <li>
            <strong>{capacites[id]?.nom ?? id}</strong>
            <span class="voie">de Classe</span>
            <span class="resume">{capacites[id]?.resume ?? ''}</span>
          </li>
        {/each}
      </ul>
    </div>
  </div>
{/if}

{#if atelier.etat.creable}
  <p class="verdict ok">Le personnage est créable.</p>
  <button type="button" class="creer" onclick={creer}>Créer le personnage</button>
  <p class="apres">
    Il rejoindra vos personnages, et le brouillon de Création sera libéré.
  </p>
  {#if refus.length > 0}
    <ul class="anomalies">
      {#each refus as message, i (message + i)}
        <li>{message}</li>
      {/each}
    </ul>
  {/if}
{:else}
  <div class="cartouche manque">
    <div class="bandeau">Ce qui manque</div>
    <div class="corps">
      <ul class="anomalies">
        {#each atelier.etat.anomalies as a, i (a.message + i)}
          <li>
            <span class="etape">{a.etape}</span>
            {a.message}
          </li>
        {/each}
      </ul>
    </div>
  </div>
{/if}

<style>
  .cartouche {
    margin-bottom: calc(var(--pas) * 4);
  }
  .manque .corps {
    padding-top: 0;
  }
  label {
    display: grid;
    gap: var(--pas);
  }
  label span {
    font-family: 'Cinzel', serif;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    font-size: 0.72rem;
    color: var(--ink-soft);
  }
  input {
    font-size: 1.05rem;
  }
  .origine {
    margin: calc(var(--pas) * 3) 0 0;
    color: var(--gold);
    font-size: 0.9rem;
  }
  .chapo {
    color: var(--ink-soft);
    font-size: 0.85rem;
    margin: 0 0 calc(var(--pas) * 3);
  }
  .derivees {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(11rem, 1fr));
    gap: var(--pas);
    margin: 0;
  }
  .derivees div {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: var(--pas);
    background: var(--surface);
    border: 1px solid var(--line);
    border-radius: var(--r);
    padding: calc(var(--pas) * 2) calc(var(--pas) * 3);
  }
  dt {
    font-size: 0.8rem;
    color: var(--ink-soft);
  }
  dd {
    margin: 0;
    font-size: 1.2rem;
    font-weight: 600;
  }
  .creer {
    font-family: 'Cinzel', serif;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    font-size: 0.85rem;
    font-weight: 700;
    background: var(--accent);
    color: var(--accent-txt);
    border: 1px solid var(--line-fort);
    border-radius: var(--r);
    min-height: 44px;
    padding: 0 calc(var(--pas) * 6);
    cursor: pointer;
  }
  .creer:hover {
    filter: brightness(1.18);
  }
  .apres {
    font-size: 0.8rem;
    color: var(--ink-soft);
    margin-top: var(--pas);
  }
  .capacites,
  .anomalies {
    list-style: none;
    padding: 0;
    margin: 0;
    display: grid;
    gap: 1px;
  }
  .capacites li {
    display: grid;
    gap: 2px;
    padding: calc(var(--pas) * 1.5) 0;
    border-bottom: 1px solid var(--line);
  }
  .capacites li:last-child {
    border-bottom: none;
  }
  .voie {
    font-size: 0.7rem;
    color: var(--gold);
    text-transform: uppercase;
    letter-spacing: 0.06em;
  }
  .resume {
    font-size: 0.85rem;
    color: var(--ink-soft);
  }
  .anomalies li {
    border-left: 2px solid var(--ko);
    padding: calc(var(--pas) * 1.5) calc(var(--pas) * 2);
    font-size: 0.9rem;
  }
  .etape {
    display: inline-block;
    min-width: 8rem;
    color: var(--ink-soft);
    font-size: 0.72rem;
    text-transform: uppercase;
    letter-spacing: 0.06em;
  }
  .verdict.ok {
    color: var(--ok);
    font-family: 'Cinzel', serif;
    letter-spacing: 0.06em;
  }
</style>
